/**
 * Request body for `addItemToReceivingInvoice`
 * Contract: see repo root `new_receiving.md` (POST /api/addItemToReceivingInvoice).
 */

import { z } from "zod";

const nullableString = z.union([z.string(), z.null()]);
const nullableStrNum = z.union([z.string(), z.number(), z.null()]);

const integerDropdownSchema = z.object({
  value: z.number().int(),
  label: z.string().optional(),
});

const conditionDropdownSchema = z.object({
  value: z.union([z.literal(1), z.literal(2)]),
  label: z.string().optional(),
});

const requiredTemperatureSchema = z.union([
  z.number().finite(),
  z.string().trim().min(1),
]);

/** Runtime schema; keep in sync with Laravel rules and `buildAddItemToReceivingInvoicePayload`. */
export const addItemToReceivingInvoicePayloadSchema = z.object({
  invoice_id: z.number(),
  assembly_item_id: z.number(),
  is_barcode: z.boolean(),
  supplier: z.record(z.string(), z.unknown()),
  supplier_plu: nullableString,
  product_name: z.string().min(1),
  lot_number: nullableString,
  received_amount: nullableStrNum,
  unit_cost: nullableStrNum,
  barcode: nullableString,
  box_date: nullableString,
  note: nullableString,
  product_condition: conditionDropdownSchema,
  product_temperature: requiredTemperatureSchema,
  product_quality: conditionDropdownSchema,
  pallet_condition: conditionDropdownSchema,
  unit_of_measure: integerDropdownSchema,
  zone: integerDropdownSchema,
  cfia_meat: nullableStrNum,
  lab_results: nullableStrNum,
  is_halal: nullableStrNum,
  number_of_cartons: z.union([z.number(), z.null()]),
});

export type AddItemToReceivingInvoicePayload = z.infer<
  typeof addItemToReceivingInvoicePayloadSchema
>;

export type NestedValueObject = { value: string | number };

export type DropdownObject = { value: string | number; label?: string };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Form: `is_barcode.value === 2` means "Barcode" mode (Laravel `is_barcode` true). */
function resolveIsBarcode(input: Record<string, unknown>): boolean {
  const raw = input.is_barcode;
  if (isRecord(raw) && "value" in raw) {
    const v = (raw as { value: unknown }).value;
    return Number(v) === 2;
  }
  if (typeof raw === "boolean") return raw;
  const n = Number(raw);
  if (n === 2) return true;
  if (n === 1) return false;
  return false;
}

function resolveSupplier(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const s = input.supplier;
  if (isRecord(s)) return { ...s };
  return {};
}

function optionalString(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v === "string") return v === "" ? null : v;
  return String(v);
}

/** Trims; empty after trim becomes null (for barcode uniqueness checks on server). */
function optionalTrimmedString(v: unknown): string | null {
  const s = optionalString(v);
  if (s === null) return null;
  const t = s.trim();
  return t === "" ? null : t;
}

function optionalNumberish(v: unknown): string | number | null {
  if (v === undefined || v === null || v === "") return null;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isNaN(n) ? v : n;
  }
  if (isRecord(v) && "value" in v) {
    return optionalNumberish((v as { value: unknown }).value);
  }
  return null;
}

function requiredInt(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function optionalInt(v: unknown): number | null {
  if (v === undefined || v === null || v === "") return null;
  if (isRecord(v) && "value" in v) {
    return optionalInt((v as { value: unknown }).value);
  }
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function normalizeScalarOrSelect(v: unknown): string | number | null {
  if (v === undefined || v === null || v === "") return null;
  if (isRecord(v) && "value" in v) {
    const inner = (v as { value: unknown }).value;
    if (inner === undefined || inner === null || inner === "") return null;
    if (typeof inner === "number" || typeof inner === "string") return inner;
    return String(inner);
  }
  if (typeof v === "number" || typeof v === "string") return v;
  return String(v);
}

/**
 * Laravel expects nested shapes for condition fields, e.g. `{ "value": "Good" }` or `{ "value": 1 }`.
 */
export function toNestedValueObject(v: unknown): NestedValueObject | null {
  if (v === undefined || v === null || v === "") return null;
  if (isRecord(v) && "value" in v) {
    const inner = (v as { value: unknown }).value;
    if (inner === undefined || inner === null || inner === "") return null;
    if (typeof inner === "number" || typeof inner === "string") {
      return { value: inner };
    }
    return { value: String(inner) };
  }
  if (typeof v === "number" && !Number.isNaN(v)) return { value: v };
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isNaN(n) ? { value: t } : { value: n };
  }
  return null;
}

function requiredTrimmedString(v: unknown): string | null {
  const s = optionalString(v);
  if (s === null) return null;
  const t = s.trim();
  return t === "" ? null : t;
}

/**
 * Laravel expects `{ value: <integer id>, label? }` for unit of measure and zone.
 * A label such as "kg" is not an id and is rejected.
 */
function toIntegerDropdown(
  v: unknown,
): { value: number; label?: string } | null {
  const dropdown = toDropdownObject(v);
  if (!dropdown) return null;

  const raw = dropdown.value;
  let id: number | null = null;
  if (typeof raw === "number" && Number.isInteger(raw)) {
    id = raw;
  } else if (typeof raw === "string" && /^-?\d+$/.test(raw.trim())) {
    id = Number(raw.trim());
  }
  if (id === null) return null;

  const result: { value: number; label?: string } = { value: id };
  if (dropdown.label) {
    result.label = dropdown.label;
  }
  return result;
}

/** Satisfactory (1) or NOT Satisfactory (2). "Choose Condition" (0) is rejected. */
function toConditionDropdown(
  v: unknown,
): { value: 1 | 2; label?: string } | null {
  const dropdown = toIntegerDropdown(v);
  if (!dropdown || (dropdown.value !== 1 && dropdown.value !== 2)) {
    return null;
  }

  const result: { value: 1 | 2; label?: string } = { value: dropdown.value };
  if (dropdown.label) {
    result.label = dropdown.label;
  }
  return result;
}

function requiredTemperature(v: unknown): string | number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") return v.trim();
  return null;
}

/**
 * Laravel expects `{ value, label? }` for location zone dropdowns.
 */
export function toDropdownObject(v: unknown): DropdownObject | null {
  if (v === undefined || v === null || v === "") return null;
  if (isRecord(v) && "value" in v) {
    const inner = (v as { value: unknown }).value;
    if (inner === undefined || inner === null || inner === "") return null;
    const result: DropdownObject = {
      value:
        typeof inner === "number" || typeof inner === "string"
          ? inner
          : String(inner),
    };
    if (typeof (v as { label?: unknown }).label === "string") {
      const label = (v as { label: string }).label.trim();
      if (label) {
        result.label = label;
      }
    }
    return result;
  }
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isNaN(n) ? { value: t } : { value: n };
  }
  if (typeof v === "number" && !Number.isNaN(v)) return { value: v };
  return null;
}

export interface BuildAddItemToReceivingInvoicePayloadContext {
  invoiceId: number;
}

/**
 * Maps loose form state into the API payload. Only keys defined by
 * `AddItemToReceivingInvoicePayload` are included (per new_receiving.md).
 */
export function buildAddItemToReceivingInvoicePayload(
  input: unknown,
  ctx: BuildAddItemToReceivingInvoicePayloadContext,
): AddItemToReceivingInvoicePayload {
  if (!isRecord(input)) {
    return addItemToReceivingInvoicePayloadSchema.parse({
      invoice_id: ctx.invoiceId,
      assembly_item_id: 0,
      is_barcode: false,
      supplier: {},
      supplier_plu: null,
      product_name: null,
      lot_number: null,
      received_amount: null,
      unit_cost: null,
      barcode: null,
      box_date: null,
      note: null,
      product_condition: null,
      product_temperature: null,
      product_quality: null,
      pallet_condition: null,
      unit_of_measure: null,
      zone: null,
      cfia_meat: null,
      lab_results: null,
      is_halal: null,
      number_of_cartons: null,
    });
  }

  const invoiceId =
    requiredInt(input.invoice_id, 0) > 0
      ? requiredInt(input.invoice_id, 0)
      : ctx.invoiceId;

  const isBarcode = resolveIsBarcode(input);
  const barcodeVal = isBarcode ? optionalTrimmedString(input.barcode) : null;

  const payload = {
    invoice_id: invoiceId,
    assembly_item_id: requiredInt(input.assembly_item_id, 0),
    is_barcode: isBarcode,
    supplier: resolveSupplier(input),
    supplier_plu: optionalString(input.supplier_plu ?? input.plu),
    product_name: requiredTrimmedString(input.product_name),
    lot_number: optionalString(input.lot_number),
    received_amount: optionalNumberish(input.received_amount),
    unit_cost: optionalNumberish(input.unit_cost),
    barcode: barcodeVal,
    box_date: optionalString(input.box_date),
    note: optionalString(input.note),
    product_condition: toConditionDropdown(input.product_condition),
    product_temperature: requiredTemperature(input.product_temperature),
    product_quality: toConditionDropdown(input.product_quality),
    pallet_condition: toConditionDropdown(input.pallet_condition),
    unit_of_measure: toIntegerDropdown(input.unit_of_measure),
    zone: toIntegerDropdown(input.zone),
    cfia_meat: normalizeScalarOrSelect(input.cfia_meat),
    lab_results: normalizeScalarOrSelect(input.lab_results),
    is_halal: normalizeScalarOrSelect(input.is_halal),
    number_of_cartons: optionalInt(input.number_of_cartons),
  };

  return addItemToReceivingInvoicePayloadSchema.parse(payload);
}
